<template>
  <div
    ref="fyin"
    class="form"
    :class="[`size-${size}`, `radius-${radius}`, {marketing}]"
  >
    <div
      v-if="bubbleShown"
      class="speech-bubble text-gray translate"
      :class="{
        'max-width': !bubbleWidth,
        'no-scale': noScale,
        'bubble-right': bubbleAlign === 'right',
      }"
      :style="{
        top: !customBubblePosition && bubbleTop + 'px',
        bottom: customBubblePosition && bubblePositionBottom + 'px',
        ...(bubbleAlign === 'right'
          ? {right: bubbleRight + 'px'}
          : {
              left: customBubblePosition
                ? bubblePositionLeft + 'px'
                : bubbleLeft + 5 + 'px',
            }),
        width: '100%',
        ...(bubbleWidth != null && {
          'max-width': `var(--px${bubbleWidth})`,
        }),
      }"
    >
      <slot name="bubble">
        {{ exclamation || info || warning }}
      </slot>
    </div>
    <div
      v-if="label"
      :class="{
        label: true,
        'abs-label': absLabel,
        translate: !excludeTranslation,
      }"
    >
      {{ label }}
      <slot name="htmlLabel" />
      <FontAwesomeIcon
        v-if="info"
        class="info-icon"
        icon="info-circle"
        @mouseenter="(e) => showBubble(e, true, 14, 10)"
        @mouseleave="(e) => showBubble(e, false)"
      />
      <div
        v-if="!noPrompt && topError && !valid"
        class="invalid-prompt text-small"
        :class="
          (valid ? 'hidden' : '') +
          (promptAlign ? ' align-' + promptAlign : ' ')
        "
      >
        {{ invalidPrompt }}
      </div>
      <div
        v-if="saved"
        class="saved-prompt text-small"
      >
        {{ savedPrompt }}
      </div>
    </div>
    <div :class="{'prefixed-input': true, 'abs-label': absLabel}">
      <SkeletonPreloader
        v-if="loading"
        class="skeleton-itm"
        width="calc(100% + var(--px2))"
        :height="
          maxHeight
            ? `${maxHeight}px`
            : size === 'md'
              ? 'var(--px48)'
              : 'var(--px32)'
        "
      />
      <div v-else>
        <FontAwesomeIcon
          v-if="icon"
          class="icon"
          :icon="`${icon}`"
        />
        <div
          v-if="preIcon"
          class="pre-icon"
        >
          <slot name="pre-icon">
            <FontAwesomeIcon
              v-if="preIcon !== true"
              :icon="preIcon"
            />
          </slot>
        </div>
        <div
          v-if="unfocus && unfocused"
          class="unfocused"
          :class="{
            'with-icon': icon,
            'with-clear': clear,
            'with-append-icon': appendIcon,
            'with-pre-icon': preIcon,
          }"
        >
          {{ unfocus }}
        </div>
        <input
          v-else-if="!multiline"
          ref="input"
          v-imask="
            mask
              ? {
                  mask: mask,
                  lazy: true,
                  definitions,
                }
              : false
          "
          :value="value"
          :autocomplete="autocomplete"
          :class="{
            disabled,
            invalid:
              !valid || (exclamation && !suppressExclamation) || inValidProp,
            valid: validProp,
            'with-icon': icon,
            'with-clear': clear,
            'with-eye': type === 'password',
            'with-append-icon': appendIcon,
            'with-pre-icon': preIcon,
            translate: !excludeTranslation,
          }"
          :disabled="disabled"
          :max="max"
          :min="min"
          :placeholder="placeholderComputed"
          :spellcheck="spellcheck"
          :style="{
            'min-height': `${maxHeight}px`,
            'max-height': `${maxHeight}px`,
          }"
          :type="type === 'number' || passShown ? 'text' : type"
          @accept="onAccept"
          @change="activate"
          @complete="onComplete"
          @focus="focusevent"
          @focusout="focusout"
          @input="
            updateValue($event);
            textIn($event);
            activate();
          "
          @keydown="getEnter"
          @keypress="activate"
        />
        <textarea
          v-else
          ref="input"
          :value="value"
          :autocomplete="autocomplete"
          :class="{
            disabled,
            multiline,
            invalid: !valid || (exclamation && !suppressExclamation),
            'with-icon': icon,
            'with-clear': clear,
            'with-eye': type === 'password',
            'with-append-icon': appendIcon,
            'with-pre-icon': preIcon,
            translate: !excludeTranslation,
          }"
          :disabled="disabled"
          :placeholder="placeholderComputed"
          :spellcheck="spellcheck"
          :type="type"
          @change="activate"
          @focus="focusevent"
          @focusout="focusout"
          @input="
            updateValue($event);
            textIn($event);
            activate();
          "
          @keydown="getEnter"
          @keypress="activate"
        />
        <div
          v-if="appendIcon"
          class="append-icon"
        >
          <slot name="append-icon">
            <FontAwesomeIcon
              v-if="appendIcon !== true"
              :icon="appendIcon"
            />
          </slot>
        </div>
        <div class="append-icon">
          <slot name="custom-content" />
        </div>
        <FontAwesomeIcon
          v-if="exclamation && !suppressExclamation"
          class="icon exclamation-icon clickable"
          icon="exclamation-circle"
          @mouseenter="(e) => showBubble(e, true, 23)"
          @mouseleave="(e) => showBubble(e, false)"
        />
        <FontAwesomeIcon
          v-if="warning"
          class="icon exclamation-icon exclamation-warning clickable"
          icon="exclamation-triangle"
          @mouseenter="(e) => showBubble(e, true, 23)"
          @mouseleave="(e) => showBubble(e, false)"
        />
        <FontAwesomeIcon
          v-if="clear && value"
          class="icon closing-icon clickable"
          icon="times"
          @click="
            value = '';
            $emit('clear');
            textIn();
          "
        />
        <FontAwesomeIcon
          v-if="search"
          class="icon closing-icon clickable"
          icon="times"
          @click="
            value = '';
            $emit('clear');
            textIn();
          "
        />
        <FontAwesomeIcon
          v-if="clearInside && value"
          class="icon closing-icon clickable"
          icon="search"
          @click="
            value = '';
            $emit('clear');
            textIn();
          "
        />
        <div class="fy-in-eye-icon">
          <FontAwesomeIcon
            v-if="type === 'password'"
            class="clickable"
            :icon="passShown ? 'eye' : 'eye-slash'"
            @click="passShown = !passShown"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!noPrompt && !topError"
      class="invalid-prompt text-small translate"
      :class="
        (valid ? 'hidden' : '') + (promptAlign ? ' align-' + promptAlign : ' ')
      "
    >
      {{ invalidPrompt }}
    </div>
    <div
      v-if="hintPrompt"
      class="translate hint-prompt text-small"
    >
      {{ hintPrompt }}
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import {IMaskDirective} from 'vue-imask';
import {nextTick} from 'vue';
import {formatMixin} from '@/mixins/formatMixin';

export default {
  name: 'FygrInput',
  directives: {
    imask: IMaskDirective,
  },
  mixins: [formatMixin],
  props: {
    marketing: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'sm',
      validator: (v) => ['sm', 'md'].includes(v),
    },
    radius: {
      type: Number,
      default: 4,
      validator: (v) => [0, 4].includes(v),
    },
    placeholder: {
      type: String,
      default: 'text',
    },
    valueIn: {
      type: [String, Number],
      default: '',
    },
    modelValue: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    allToLower: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    info: {
      type: String,
      default: '',
    },
    min: {
      type: String,
      default: '',
    },
    max: {
      type: String,
      default: '',
    },
    unfocus: {
      type: String,
      default: '',
    },
    appendIcon: {
      type: [String, Number],
      default: '',
    },
    preIcon: {
      type: [String, Number],
      default: '',
    },
    autofocus: Boolean,
    noPrompt: Boolean,
    validate: Boolean,
    disabled: Boolean,
    noScale: Boolean,
    bubble: {
      type: Number,
      default: null,
    },
    bubbleWidth: {
      type: Number,
      default: null,
    },
    bubblePositionLeft: {
      type: Number,
      default: null,
    },
    bubblePositionBottom: {
      type: Number,
      default: null,
    },
    bubbleAlign: {
      type: String,
      default: 'left',
    },
    bubbleDeltaX: {
      type: Number,
      default: null,
    },
    bubbleDeltaY: {
      type: Number,
      default: null,
    },
    aboveZero: Boolean,
    exclamation: {
      type: String,
      default: '',
    },
    warning: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    maxHeight: {
      type: Number,
      default: null,
    },
    mask: {
      type: [String, RegExp],
      default: '',
    },
    promptAlign: {
      type: String,
      default: '',
    },
    absLabel: {
      type: Boolean,
      default: false,
    },
    clear: {
      type: Boolean,
      default: false,
    },
    clearInside: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
    spellcheck: {
      type: Boolean,
      default: true,
    },
    autocomplete: {
      type: String,
      default: '',
    },
    scrollTop: {
      type: Boolean,
      default: false,
    },
    savedPrompt: {
      type: String,
      default: 'Saved',
    },
    saved: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    isValid: {
      type: Function,
      default: () => true,
    },
    multiline: {
      type: Boolean,
      default: false,
    },
    topError: {
      type: Boolean,
      default: false,
    },
    tab: {
      type: Boolean,
      default: false,
    },
    inValidProp: {
      type: Boolean,
      default: false,
    },
    validProp: {
      type: Boolean,
      default: false,
    },
    invalidPrompt: {
      type: String,
      default: 'Input is invalid',
    },
    hintPrompt: {
      type: String,
      default: null,
    },
    customBubblePosition: {
      type: Boolean,
      default: false,
    },
    excludeTranslation: {
      type: Boolean,
      default: false,
    },
    excludeTranslationPlaceholder: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:modelValue',
    'tab',
    'esc',
    'next',
    'activate',
    'clear',
    'input',
    'mask-valid',
    'focus',
    'focusout',
    'validation',
  ],
  data() {
    return {
      bubbleRight: 0,
      bubbleLeft: 0,
      bubbleShown: false,
      bubbleTop: 0,
      definitions: {
        '*': /[.,]/,
        '%': /%/,
      },
      value:
        this.valueIn !== null || this.valueIn !== undefined ? this.valueIn : '',
      interval: null,
      passShown: false,
      suppressExclamation: false,
      unfocused: true,
      bubbleAlignLeft: false,
      bubbleRemover: null,
      placeholderComputed: this.placeholder,
    };
  },
  computed: {
    ...mapState('app', ['scaleRatio']),
    ...mapGetters('user', ['getLocaleIso', 'getLocale']),
    valid() {
      this.$emit('validation', this.isValid(this.value) && !this.invalid);
      return !this.validate || (this.isValid(this.value) && !this.invalid);
    },
  },
  watch: {
    valueIn() {
      this.resetValue();
    },
    getLocaleIso: {
      immediate: true,
      handler() {
        this.translatePlaceHolder();
      },
    },
    autofocus() {
      if (this.autofocus) {
        setTimeout(() => this.focus());
      }
    },
  },
  async mounted() {
    if (this.autofocus) {
      setTimeout(() => {
        this.focus();
        this.resetValue();
      });
    }
    if (this.type === 'number') {
      const invalidChars = ['+', 'e'];
      this.$refs.input.addEventListener('keydown', (e) => {
        if (invalidChars.includes(e.key)) {
          e.preventDefault();
        }
      });
      this.resetValue();
    }
  },
  methods: {
    ...mapActions('translations', ['actionTranslatePhrase']),
    async translatePlaceHolder() {
      if (
        this.excludeTranslation &&
        !this.excludeTranslationPlaceholder &&
        this.placeholder !== 'text'
      ) {
        this.placeholderComputed = await this.actionTranslatePhrase(
          this.placeholder,
        );
      }
    },
    focusout() {
      this.unfocused = true;
      this.suppressExclamation = false;
      this.$emit('focusout', this.value);
    },
    focusevent(e) {
      this.suppressExclamation = true;
      if (this.scrollTop) {
        this.$refs.fyin.scrollIntoView(true);
      }
      this.$emit('focus', e);
    },
    onAccept(e) {
      if (!this.mask) return;
      const maskRef = e.detail;
      this.value = maskRef.value;
      this.$emit('mask-valid', false);
      this.$emit('input', this.value);
    },
    onComplete() {
      this.$emit('input', this.value);
      this.$emit('mask-valid', true);
    },
    textIn(e) {
      if (this.mask) return;
      if (this.aboveZero) {
        this.value = this.value.replace(/\D/g, '');
      }
      if (this.type === 'number') {
        const el = e.target;
        const oldValue = el.value;
        const oldPos = el.selectionStart;
        this.value = this.replaceSeparators(this.value);
        if (this.value === '-' || this.value === '') {
          this.$emit('input', this.value);
          return;
        }
        const parsed = this.parseLocalizedNumber(this.value);
        if (!parsed || parsed.isEmpty) return null;
        let toReturn = parsed.num;

        if (!!this.min && Number(this.min) > toReturn) {
          toReturn = this.value = this.min;
        }
        if (!!this.max && Number(this.max) < toReturn) {
          toReturn = this.value = this.max;
        }

        this.$emit('input', toReturn);
        // return caret to prev position
        this.$nextTick(() => {
          const diff = this.value.length - oldValue.length;
          const newPos = Math.max(0, oldPos + diff);
          el.setSelectionRange(newPos, newPos);
        });
        return;
      }
      if (this.allToLower) {
        this.$emit('input', this.value.toLowerCase());
        return;
      }
      this.$emit('input', this.value);
    },
    updateValue(e) {
      this.value = e.target.value;
      this.$emit('update:modelValue', e.target.value);
    },
    activate() {
      this.$emit('activate');
    },
    showBubble(e, show, fixY, fixX) {
      if (this.customBubblePosition) {
        this.bubbleShown = show;
        return;
      }
      const leftEdge = e.clientX - e.offsetX;
      this.bubbleRight =
        window.innerWidth -
        leftEdge +
        (20 + this.bubbleDeltaX) / this.scaleRatio;

      if (this.noScale) {
        this.bubbleTop =
          e.clientY - e.offsetY - (fixY + this.bubbleDeltaY) / this.scaleRatio;
        this.bubbleLeft =
          e.clientX - e.offsetX + (30 + this.bubbleDeltaX) / this.scaleRatio;
      } else {
        this.bubbleTop = e.clientY - e.offsetY - fixY / this.scaleRatio;
        this.bubbleLeft =
          e.clientX - e.offsetX + 30 - (fixX || 0) / this.scaleRatio;
      }
      this.bubbleShown = show;
    },
    focus() {
      this.unfocused = false;
      nextTick(() => this.$refs?.input?.focus());
      this.$emit('focus');
    },
    removeBubble(e) {
      this.bubbleRemover = setTimeout(() => this.showBubble(e, false), 400);
    },
    cancelRemove() {
      clearTimeout(this.bubbleRemover);
    },
    getEnter(e) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return true;
      }
      if (e.keyCode === 8 && this.type === 'number') {
        this.onBackspace(e);
      }
      if (e.keyCode === 13) {
        e.preventDefault();
        this.$emit('next', this.value);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.$emit('esc');
      } else if (e.keyCode === 9 && this.tab) {
        e.preventDefault();
        this.$emit('tab');
      }
      return true;
    },
    getInput() {
      return this.$refs.input;
    },
    resetValue() {
      this.value =
        this.type === 'number'
          ? this.replaceSeparators(this.valueIn)
          : this.valueIn;
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.speech-bubble {
  position: fixed;
  background: white;
  border-radius: 4px;
  text-align: left;
  padding: var(--px10);
  -webkit-box-shadow: var(--px2) var(--px2) var(--px20) var(--px7)
    rgba(0, 0, 0, 0.25);
  -moz-box-shadow: var(--px2) var(--px2) var(--px20) var(--px7)
    rgba(0, 0, 0, 0.25);
  box-shadow: var(--px2) var(--px2) var(--px20) calc(var(--px5) * -1)
    rgba(0, 0, 0, 0.25);
  animation-name: expand-dialog, appear;
  animation-duration: 0.4s;
  z-index: 100;
  &.max-width {
    max-width: var(--px190);
    &.no-scale {
      max-width: var(--px190);
    }
  }
  &.no-scale {
    padding: var(--px20);
    border-radius: var(--px4);
    -webkit-box-shadow: var(--px2) var(--px2) var(--px20) var(--px7)
      rgba(0, 0, 0, 0.25);
    -moz-box-shadow: var(--px2) var(--px2) var(--px20) var(--px7)
      rgba(0, 0, 0, 0.25);
    box-shadow: var(--px2) var(--px2) var(--px20) var(--px-5)
      rgba(0, 0, 0, 0.25);
    font-size: var(--px14);
  }
  &.left:before {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    width: 0;
    height: 0;
    border: var(--px5) px solid transparent;
    border-left-color: white;
    border-right: 0;
    margin-top: var(--px-5);
    margin-right: var(--px-5);
  }
  &.left:after {
    border: none;
    margin: 0;
  }
}
.speech-bubble:after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 0;
  height: 0;
  border: var(--px6) solid transparent;
  border-right-color: white;
  border-left: 0;
  margin-top: var(--px-5);
  margin-left: var(--px-5);
}
.speech-bubble.no-scale:after {
  margin-top: var(--px-5);
  margin-left: var(--px-5);
  font-size: var(--px14);
}
.speech-bubble.bubble-right:after {
  left: auto;
  top: 10%;
  right: 0;
  border-left-color: white;
  margin-left: 0;
  margin-right: var(--px-5);
  transform: rotate(180deg);
}
.label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  color: $text-fade;
  font-weight: 500;
  font-size: var(--px12);
  &.abs-label {
    position: absolute;
    top: 0;
    left: 0;
    white-space: nowrap;
  }
  .info-icon {
    display: flex;
    color: $aubergine;
    justify-self: flex-start;
    margin-left: var(--px10);
    margin-right: auto;
  }
  .invalid-prompt {
    font-style: italic;
  }
}
form,
div,
form {
  display: flex;
  flex-direction: column;
  position: relative;
}
.form {
  gap: var(--px4);
}
.form.size-md {
  gap: var(--px8);
}
.prefixed-input {
  position: relative;
  display: flex;
  &.abs-label {
    margin-top: var(--px19);
  }
}
.fy-in-eye-icon {
  position: absolute;
  height: 100%;
  top: 0;
  right: var(--px15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $gray;
}
.icon {
  left: var(--px15);
  top: 50%;
  transform: translateY(-50%);
  position: absolute;
  color: $gray;
  pointer-events: none;
  &.closing-icon {
    right: var(--px15);
    left: unset;
    pointer-events: all;
  }
  &.exclamation-icon {
    right: var(--px10);
    left: unset;
    color: $red;
    pointer-events: all;
  }
  &.exclamation-warning {
    color: $selection-yellow;
  }
}
.unfocused {
  display: flex;
  justify-content: center;
  color: $text-fade;
}
input,
textarea,
.unfocused {
  box-sizing: border-box;
  width: 100%;
  flex: 1;
  border-radius: var(--px4);
  max-height: var(--px32);
  min-height: var(--px32);
  text-decoration: none;
  border: 1px solid $stroke;
  background-color: #ffffff;
  outline: 0;
  padding: 0 var(--px10);
  font-weight: normal;
  font-size: var(--px13);
  font-family: $font-family;
  -webkit-appearance: none;
  &.multiline {
    width: 100%;
    resize: none;
    padding: var(--px10);
    max-height: none;
    min-height: auto;
    flex: 1;
  }
  &:placeholder-shown {
    color: $text-fade;
  }
  &:focus {
    color: $text-primary;
    border: 1.5px solid $aubergine;
  }
  &.disabled {
    background-color: #fafaf8;
    opacity: 0.5;
    color: color.adjust($text-fade, $lightness: 10%);
    &::placeholder {
      color: color.adjust($text-fade, $lightness: 25%);
    }
  }
  &.invalid {
    border: 1.5px solid $red;
  }
  &.valid {
    @include focus-ring($success);
  }
  &.with-icon {
    padding-left: var(--px45);
  }
  &.with-eye {
    padding-right: var(--px45);
  }
  &.with-clear {
    padding-right: var(--px45);
  }
  &.with-append-icon {
    padding-right: var(--px25);
  }
  &.with-pre-icon {
    padding-left: var(--px49);
  }
}
.invalid-prompt {
  user-select: none;
  display: flex;
  color: $red;
  font-size: var(--px11);
  &.hidden {
    color: rgba($white, 0);
  }
}
.form.size-md > .invalid-prompt {
  margin-top: var(--px6);
  font-size: var(--px12);
}
@media only screen and (max-width: 950px) {
  .invalid-prompt {
    text-align: right;
  }
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type='number'] {
  -moz-appearance: textfield;
}
.append-icon {
  display: flex;
  align-items: center;
  position: absolute;
  right: var(--px15);
  top: 30%;
  i {
    font-weight: 400;
  }
}
.pre-icon {
  display: flex;
  align-items: center;
  position: absolute;
  left: var(--px15);
  top: calc(50% - var(--px1));
  i {
    font-weight: 400;
  }
}
.align-right {
  text-align: right;
}
.align-left {
  text-align: left;
}
.hint-prompt {
  position: absolute;
  bottom: -18px;
  right: -3px;
  color: $text-fade;
}
.form.size-md {
  .label {
    font-size: var(--px14);
  }
  input,
  textarea,
  .unfocused {
    max-height: var(--px48);
    min-height: var(--px48);
    padding: 0 var(--px12);
    font-size: var(--px14);
  }
}
.form.radius-0 {
  input,
  textarea,
  .unfocused {
    border-radius: 0;
  }
}
/* Marketing variant — flat 40px input used in the signup / onboarding flow. */
.form.marketing {
  input,
  textarea,
  .unfocused {
    height: var(--px40);
    min-height: var(--px40);
    max-height: var(--px40);
    border-radius: 0;
    font-family: 'Geist', 'Inter', sans-serif;
    font-size: var(--px14);
    font-weight: 500;
  }
}
</style>
